import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import { ApiError, intake } from "../../lib/api";
import { withUtm } from "../../lib/utm";
import { useSeo } from "../../lib/useSeo";
import { Btn } from "../../lib/Btn";
import { Card } from "../../lib/Card";
import { Icon } from "../../lib/Icon";
import { Input, Textarea } from "../../lib/Input";
import { Pill } from "../../lib/Pill";
import { Slats } from "../../lib/Slats";

interface ContactForm {
  name: string;
  email: string;
  company: string;
  phone: string;
  facility_count: string;
  message: string;
}

const EMPTY: ContactForm = {
  name: "",
  email: "",
  company: "",
  phone: "",
  facility_count: "",
  message: "",
};

export function ContactPage() {
  useSeo({
    title: "Contact sales — NestPlot",
    description: "Get in touch with NestPlot sales. We typically respond within one business day. info@nestplot.com.",
    path: "/contact",
  });
  const [form, setForm] = useState<ContactForm>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      intake.submitInquiry(withUtm({
        name: form.name,
        email: form.email,
        company: form.company || undefined,
        phone: form.phone || undefined,
        facility_count: form.facility_count ? Number(form.facility_count) : undefined,
        message: form.message,
        source: "contact-page",
      })),
    onSuccess: () => {
      toast.success("We'll get back to you within 1 business hour");
      setForm(EMPTY);
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError && err.status === 429
          ? "Hit the rate limit — give us a moment and try again."
          : "Send failed. Email us directly at info@nestplot.com.";
      setError(msg);
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.name || !form.email || !form.message) {
      setError("Name, email, and message are required");
      return;
    }
    submit.mutate();
  }

  return (
    <div className="mx-auto max-w-[1080px] px-5 lg:px-8 py-12 lg:py-16 grid lg:grid-cols-[1fr_1fr] gap-10">
      <div>
        <Pill tone="accent" size="sm">
          Talk to a human
        </Pill>
        <h1 className="t-display-xl mt-4">Get in touch</h1>
        <p className="text-sub text-lg mt-3 max-w-[480px]">
          Have a question we didn't answer on the site? Want to know whether your access
          vendor — building fob, gate code, LPR, or dock pass — works with us? Drop a
          note. We answer every email within one business hour, 7 days a week.
        </p>
        <div className="mt-8 space-y-4 text-sm">
          <Row icon="doc" label="Email" value="info@nestplot.com" link="mailto:info@nestplot.com" />
          <Row
            icon="home"
            label="Mailing address"
            value="NestPlot · 16192 Coastal Highway · Lewes, DE 19958-9776"
          />
        </div>
        <div className="mt-8">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase mb-3">
            Already a customer?
          </div>
          <ul className="text-sm space-y-2">
            <li>
              · Operator dashboard ·{" "}
              <a href="https://app.nestplot.com" className="text-accent font-semibold">
                app.nestplot.com
              </a>
            </li>
            <li>
              · Tenant portal ·{" "}
              <a href="https://portal.nestplot.com" className="text-accent font-semibold">
                portal.nestplot.com/&lt;facility&gt;
              </a>
            </li>
          </ul>
        </div>
      </div>

      <Card>
        <div className="bg-accent px-6 pt-3 pb-2">
          <Slats color="var(--on-accent)" opacity={0.55} height={10} />
        </div>
        <form onSubmit={onSubmit} className="px-6 py-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Your name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              autoComplete="name"
            />
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              autoComplete="email"
            />
            <Input
              label="Company / facility"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              autoComplete="organization"
            />
            <Input
              label="Phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              autoComplete="tel"
            />
          </div>
          <Input
            label="How many facilities?"
            type="number"
            value={form.facility_count}
            onChange={(e) => setForm({ ...form, facility_count: e.target.value })}
            hint="Helps us tee up the right plan"
          />
          <Textarea
            label="What's on your mind?"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            rows={5}
            required
            placeholder="Currently on AppFolio for our 48-unit apartment building, looking to migrate this summer — or whatever's true for you (storEDGE on 3 storage facilities, Marina Office Manager on a 120-slip yard, a spreadsheet on a 60-stall lot, etc.)…"
          />
          {error && <div className="text-alert text-sm">{error}</div>}
          <div className="flex items-center justify-between gap-3 pt-1">
            <span className="text-[11.5px] text-sub font-mono uppercase tracking-[0.08em]">
              <Icon name="lock" size={11} className="inline mr-1" /> We never share your details.
            </span>
            <Btn kind="primary" size="md" type="submit" disabled={submit.isPending}>
              {submit.isPending ? "Sending…" : "Send message"}
            </Btn>
          </div>
        </form>
      </Card>
    </div>
  );
}

function Row({
  icon,
  label,
  value,
  link,
}: {
  icon: "phone" | "doc" | "home" | "lock";
  label: string;
  value: string;
  link?: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-md bg-surface border border-line grid place-items-center text-accent shrink-0">
        <Icon name={icon} size={15} />
      </div>
      <div>
        <div className="font-mono text-[10px] text-sub tracking-[0.12em] uppercase">{label}</div>
        {link ? (
          <a href={link} className="font-semibold text-ink hover:text-accent">
            {value}
          </a>
        ) : (
          <div className="font-semibold">{value}</div>
        )}
      </div>
    </div>
  );
}
